"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Search, User, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223852" className="flex gap-6">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-2xl" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-2xl" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-2xl" />
      </div>;
  }
  const linkClasses = "relative text-sm font-medium font-default text-foreground py-2 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full";
  return <>
      {}
      <NavigationMenu className="hidden md:flex" viewport={false}>
        <NavigationMenuList className="gap-8">
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={linkClasses}>
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {}
      <div className="md:hidden">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Menu">
              <Menu className="size-6 text-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-4 flex flex-col gap-2 mt-2 rounded-2xl border-border bg-background/95 backdrop-blur-xl shadow-xl">
            {menuItems.map((item, i) => <div key={item.id} data-index={i}>
                <Link to={item.href || "#"} className="block text-base font-medium font-default text-foreground py-3 border-b border-border/50 hover:text-primary transition-colors">
                  {item.label}
                </Link>
              </div>)}
          </PopoverContent>
        </Popover>
      </div>
    </>;
}
export default function TestataFelinova() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="the-header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b text-foreground", isScrolled ? "bg-background/80 backdrop-blur-xl border-border shadow-md" : "bg-background border-transparent")}>
      <header className="container mx-auto px-4 lg:px-8 h-20 flex items-center justify-between relative">
        {}
        <div className="flex-1 flex items-center justify-start">
          <MenuProvider menu_id="26">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <Link to="/" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/felinova_logo.png" alt="Petit Pop Toys" className="h-8 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex items-center justify-end gap-1 md:gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Cerca">
                <Search className="size-5 text-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3 mt-2 rounded-2xl border-border bg-background shadow-xl">
              <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Find your obsession..." />
            </PopoverContent>
          </Popover>

          <Button variant="ghost" size="icon" asChild aria-label="Account">
            <Link to="#">
              <SafeHydrate><Avatar className="size-6 bg-transparent">
                <AvatarFallback className="bg-transparent text-foreground">
                  <User className="size-5" />
                </AvatarFallback>
              </Avatar></SafeHydrate>
            </Link>
          </Button>

          <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-foreground" />
        </div>
      </header>
    </section>;
}